import { useCustomAttributeFilters } from "#hooks/FilterPanel/useCustomAttributeFilters";
import {
  useTableSortingAndPagination,
  useTablesSortingAndPagination,
} from "#redux/reducers/tableStateReducer";
import { linkToFacilities } from "#routers/links";
import { ImportDataAction } from "#src/batteries-included-components/Buttons/ImportDataAction";
import PermissionAwareExportButton from "#src/batteries-included-components/Buttons/PermissionAwareExportButton/PermissionAwareExportButton";
import EditFacilityDialog from "#src/batteries-included-components/Dialogs/EditFacilityDialog";
import {
  FacilitiesTableFilterArea,
  FacilitiesViewFilterArea,
} from "#src/batteries-included-components/FilterAreas/AssetsFilterAreas";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import {
  useDataTableColumnConfigurationProps,
  useDataTableGroupBy,
  useDataTableGroupByProps,
  useDataTableMetaExtendedHeaders,
  useDataTableMetaHeaders,
  useDataTableUserInterfaceConfiguration,
} from "#src/utils/table";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
  Button,
  DataTablePanel,
  Dialog,
  getDataTableGroupByKeysForGroupByData,
  HeaderType,
  ItemAction,
  Pill,
  StorageKeys,
  useToast,
} from "@validereinc/common-components";
import {
  AssetType,
  FacilityAdapter,
  FacilityAdapterQueryKeys,
  FacilityType,
  ResourceDefinitions,
  SortDirection,
  type GroupedByEntryType,
} from "@validereinc/domain";
import {
  downloadLink,
  getYearMonthFromDateRange,
  toFlattenedObject,
  toTitleCaseString,
} from "@validereinc/utilities";
import React, { useMemo, useState } from "react";

export const FacilityTablePanel = ({
  filterConfigStorageKey,
  viewConfigStorageKey,
  tableConfigStorageKey,
  tableStateStorageKey,
  groupByTablesStateStorageKey,
}: Pick<
  StorageKeys,
  | "filterConfigStorageKey"
  | "viewConfigStorageKey"
  | "tableConfigStorageKey"
  | "tableStateStorageKey"
  | "groupByTablesStateStorageKey"
>) => {
  const { toast } = useToast();
  const { localize, isLoading: isMappingLoading } = useLocalization();
  const [isDataIngestionEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:data_pipeline",
  });
  const [assetIdToEdit, setAssetIdToEdit] = useState<string | undefined>();
  const [facilityToDelete, setFacilityToDelete] = useState<FacilityType>();
  const [isExporting, setIsExporting] = useState(false);
  const [viewFilters] = useStickyState({}, viewConfigStorageKey);
  const [tableFilters] = useStickyState({}, filterConfigStorageKey);
  const rawFilters = useMemo(
    () => ({ ...tableFilters, ...viewFilters }),
    [tableFilters, viewFilters]
  );
  const {
    saveDefaultConfigurationMutation,
    isSaveConfigurationAvailable,
    resetConfigurationMutation,
    deleteDefaultConfigurationMutation,
    isDeleteConfigurationAvailable,
    isLoading: isLoadingTableUserInterfaceConfiguration,
    matchingDefaultConfiguration,
    matchingDefaultTableUserInterfaceConfigurationsQuery,
  } = useDataTableUserInterfaceConfiguration(tableConfigStorageKey);

  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "name",
      sortDirection: SortDirection.ASCENDING,
    },
    rawFilters,
    tableStateStorageKey
  );

  const { customAttributeHeaders, isLoading: isCustomAttributesLoading } =
    useCustomAttributeFilters({
      assetType: AssetType.FACILITY,
      groupable: true,
    });

  const { savedFilter: _, period, ...filters } = rawFilters as any;
  const params = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    groupBy: tableState.groupBy,
    filters: toFlattenedObject(filters, {
      transformObjectValue: (val) => val?.id,
    }),
    period: getYearMonthFromDateRange(period),
  };

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: FacilityAdapterQueryKeys.getList(params),
    queryFn: () => FacilityAdapter.getList(params),
  });
  const items = data?.data ?? [];

  const groupByKeys = getDataTableGroupByKeysForGroupByData(
    items as unknown as GroupedByEntryType[]
  );
  const [groupByTablesStates, updateGroupByTablesStates] =
    useTablesSortingAndPagination(
      groupByKeys,
      {
        ...params,
        page: 1,
        pageSize: 10,
        sortBy: "created_at",
        sortDirection: SortDirection.DESCENDING,
      },
      groupByTablesStateStorageKey
    );

  const {
    queryPayloads: groupByQueryPayloads,
    setActiveGroupByGroups,
    totalSelectableItems,
  } = useDataTableGroupBy(
    items as unknown as GroupedByEntryType[],
    tableState.groupBy,
    groupByKeys.map((key) => groupByTablesStates.get(key) ?? params)
  );

  const facilitiesGroupedByQueries = useQueries({
    queries: groupByQueryPayloads.map((d) => ({
      queryKey: FacilityAdapterQueryKeys.getList(d.payload),
      queryFn: () => FacilityAdapter.getList(d.payload),
      enabled: d.enabled,
    })),
  });

  const metaHeaders = useDataTableMetaHeaders();
  const metaExtendedHeaders = useDataTableMetaExtendedHeaders(
    tableState.groupBy.length
      ? facilitiesGroupedByQueries.flatMap((q) => q.data?.data ?? [])
      : items
  );

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const { name: file_name, s3_download_link } =
        await FacilityAdapter.exportList!({
          ...params,
        });

      downloadLink(s3_download_link, file_name);
    } catch (err) {
      console.error(err);
    } finally {
      setIsExporting(false);
    }
  };

  const deleteFacilityOnClick = async () => {
    try {
      if (facilityToDelete) {
        await FacilityAdapter.deleteOne({
          id: facilityToDelete.id,
        });
        toast.push({
          intent: "success",
          description: `Successfully deleted '${facilityToDelete.name}'`,
        });
      }
    } finally {
      refetch();
      setFacilityToDelete(undefined);
    }
  };

  const staticHeaders: Array<HeaderType<FacilityType>> = useMemo(
    () => [
      {
        label: `${localize("Facility")} Name`,
        key: "name",
        isSortable: true,
        isGroupable: true,
        renderComponent: ({ item }) => (
          <RoutingLink to={linkToFacilities(item?.id)}>{item.name}</RoutingLink>
        ),
      },
      {
        label: "Status",
        key: "status",
        isSortable: true,
        isGroupable: true,
        renderComponent: ({ item }) => (
          <Pill variant={item.status === "active" ? "success" : "default"}>
            {toTitleCaseString(item.status, {
              intent: "ui",
              exceptions: ["oil-and-gas"],
            })}
          </Pill>
        ),
      },
    ],
    [localize]
  );

  const headers = useMemo(
    () => [
      ...staticHeaders,
      ...customAttributeHeaders,
      ...metaHeaders,
      ...metaExtendedHeaders,
    ],
    [staticHeaders, customAttributeHeaders, metaHeaders, metaExtendedHeaders]
  );

  const actionRow = (
    <>
      <PermissionAwareExportButton
        key="export-facilities"
        onClick={handleExport}
        isExporting={isExporting}
        isDisabled={tableState.groupBy.length > 0}
      />
      {isDataIngestionEnabled ? (
        <ImportDataAction
          key="import-facility"
          resource={{
            ...ResourceDefinitions.facility,
            label: {
              singular: localize(ResourceDefinitions.facility.label.singular),
              plural: localize("facility_plural"),
            },
          }}
        />
      ) : null}
    </>
  );

  const getItemActions = ({ item }: { item: FacilityType }): ItemAction[] => [
    {
      label: "Edit",
      buttonProps: {
        onClick: () => setAssetIdToEdit(item.id),
        icon: "pencil-simple",
      },
    },
    {
      label: "Delete",
      buttonProps: {
        onClick: () => setFacilityToDelete(item),
        icon: "trash",
      },
    },
  ];

  const groupByDataTableProps = useDataTableGroupByProps({
    items,
    getItemId: (item) => item.id,
    groupBy: tableState.groupBy,
    updateTableState,
    setActiveGroupByGroups,
    groupByQueries: facilitiesGroupedByQueries,
    groupByTablesStates,
    updateGroupByTablesStates,
    headers,
    getItemActions,
    totalSelectableItems,
  });
  const columnConfigurationDataTableProps =
    useDataTableColumnConfigurationProps({
      configurationName: "Default for Facilities list table",
      saveDefaultConfigurationMutation,
      isSaveConfigurationAvailable,
      resetConfigurationMutation,
      deleteDefaultConfigurationMutation,
      isDeleteConfigurationAvailable,
      isLoading: isLoadingTableUserInterfaceConfiguration,
      matchingDefaultConfiguration,
      matchingDefaultTableUserInterfaceConfigurationsQuery,
    });

  const loading = isLoading || isMappingLoading;

  return (
    <>
      <FacilitiesViewFilterArea viewConfigStorageKey={viewConfigStorageKey} />
      <DataTablePanel
        storageKey={tableConfigStorageKey}
        panelProps={{
          actionRow,
        }}
        filterComponent={
          <FacilitiesTableFilterArea
            filterConfigStorageKey={filterConfigStorageKey}
          />
        }
        columnConfigurationProps={columnConfigurationDataTableProps}
        dataTableProps={{
          intent: "simplicity-first",
          headers,
          items,
          getItemId: (item) => item.id,
          isLoading: loading || isCustomAttributesLoading,
          isBusy: isFetching,
          getItemActions,
          onSortChange: updateTableState,
          onPaginationChange: updateTableState,
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: data?.total_entries,
          },
        }}
        {...groupByDataTableProps}
      />
      <Dialog
        isOpen={!!facilityToDelete}
        onClose={() => setFacilityToDelete(undefined)}
        title={`Delete "${facilityToDelete?.name}"`}
        actionRow={[
          <Button
            key="delete-dialog-action"
            onClick={deleteFacilityOnClick}
            variant="error"
          >
            Delete
          </Button>,
        ]}
      >
        <div>
          Are you sure you want to delete this {localize("facility")}? This
          action cannot be undone.
        </div>
      </Dialog>
      <EditFacilityDialog
        defaultPeriod={getYearMonthFromDateRange(period) ?? ""}
        facilityId={assetIdToEdit}
        onClose={() => setAssetIdToEdit(undefined)}
        onEdit={() => {
          refetch();
        }}
      />
    </>
  );
};
